import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSocioMisProyectos, type SocioProyecto } from "@/hooks/usePortalSocios";
import { useSocioViewAsEmail } from "./SocioImpersonationContext";

/**
 * Proyecto activo del Portal de Socios.
 * La lista sale de `socio_mis_proyectos()`; la selección se recuerda por usuario en
 * localStorage (sólo conveniencia: si el storage falla, se toma el primer proyecto).
 * En "Ver como socio" la lista es la del socio impersonado y la selección se reinicia
 * (no se lee ni se escribe el storage del admin).
 */
interface SocioProyectoContextValue {
  proyectos: SocioProyecto[];
  idProyecto: number | null;
  proyectoActual: SocioProyecto | null;
  setIdProyecto: (id: number) => void;
  isLoading: boolean;
  /** socio_mis_proyectos() aún no existe en BD. */
  pendiente: boolean;
  error: unknown;
}

const SocioProyectoContext = createContext<SocioProyectoContextValue | null>(null);

const storageKey = (uid: string | null | undefined) => `sozu.portal-socios.proyecto.${uid ?? "anon"}`;

function leer(key: string): number | null {
  try {
    const v = window.localStorage.getItem(key);
    const n = v == null ? NaN : Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function escribir(key: string, id: number) {
  try {
    window.localStorage.setItem(key, String(id));
  } catch {
    /* storage bloqueado: la selección vive sólo en memoria */
  }
}

export function SocioProyectoProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const viewAs = useSocioViewAsEmail();
  const key = storageKey(user?.id);
  const q = useSocioMisProyectos(viewAs);
  const proyectos = useMemo(() => q.data?.data ?? [], [q.data]);
  const [seleccion, setSeleccion] = useState<number | null>(() => (viewAs ? null : leer(key)));

  // Cambiar de usuario o de socio impersonado reinicia el proyecto seleccionado.
  useEffect(() => {
    setSeleccion(viewAs ? null : leer(key));
  }, [key, viewAs]);

  const idProyecto = useMemo(() => {
    if (!proyectos.length) return null;
    if (seleccion != null && proyectos.some((p) => p.id_proyecto === seleccion)) return seleccion;
    return proyectos[0].id_proyecto;
  }, [proyectos, seleccion]);

  const setIdProyecto = useCallback(
    (id: number) => {
      setSeleccion(id);
      if (!viewAs) escribir(key, id);
    },
    [key, viewAs],
  );

  const value = useMemo<SocioProyectoContextValue>(
    () => ({
      proyectos,
      idProyecto,
      proyectoActual: proyectos.find((p) => p.id_proyecto === idProyecto) ?? null,
      setIdProyecto,
      isLoading: q.isLoading,
      pendiente: !!q.data?.pendiente,
      error: q.error,
    }),
    [proyectos, idProyecto, setIdProyecto, q.isLoading, q.data?.pendiente, q.error],
  );

  return <SocioProyectoContext.Provider value={value}>{children}</SocioProyectoContext.Provider>;
}

export function useSocioProyecto(): SocioProyectoContextValue {
  const ctx = useContext(SocioProyectoContext);
  if (!ctx) {
    throw new Error("useSocioProyecto debe usarse dentro de <PortalSociosLayout> (SocioProyectoProvider).");
  }
  return ctx;
}
